import { merchantApi } from './MerchantManager';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Scene } from '../../enums';
import { useTranslation } from 'react-i18next';
import Panel from '../../components/Panel/Panel';
import { JButton } from '../../components/UI/JButton';
import { CDNImage } from '../../components/Elements/Image';
import { useAppSelector } from '../../hooks';
import { IItem } from '../../slices/inventorySlice';
import { getItemAmount, getItemLocation, getItemSrc, isItemBound } from '../../resources/Items';
import { ChatItemLink } from '../Chat/ChatItemLink';
import { cancelFromServer, fromServer, toServer } from '../../util/ServerSocket';
import './Mailbox.css';

interface Mail {
  id: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  price: number;
  attachments: IItem[];
  state: string;
  read: boolean;
  createdAt: number;
}
interface MailList {
  messages: Mail[];
  more: boolean;
  unread: number;
}
export function mailRequest<T>(event: string, data: unknown): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('mail_error_timeout')), 15000);
    toServer(event, data, (reply) => {
      clearTimeout(timer);
      if (reply?.ok) resolve(reply.data);
      else reject(new Error(reply?.error ?? 'mail_error_failed'));
    });
  });
}
function requestId() {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export function Mailbox() {
  const { t } = useTranslation();
  const fight = useAppSelector((state) => state.fight?.fight);
  const scene = useAppSelector((state) => state.scene?.scene);
  const visible = !!fight || [Scene.FIRE_VILLAGE, Scene.WATER_VILLAGE, Scene.LIGHTNING_VILLAGE, Scene.WIND_VILLAGE, Scene.EARTH_VILLAGE].includes(scene);
  const [fightContainer, setFightContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setFightContainer(fight ? document.getElementById('fightContainer') : null);
  }, [fight]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);
  const refresh = useCallback(() => {
    setRevision((value) => value + 1);
    mailRequest<MailList>('mailList', { folder: 'inbox', page: 0 })
      .then((data) => setUnread(data.unread))
      .catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    fromServer('mailChanged', refresh);
    return () => {
      cancelFromServer('mailChanged', refresh);
    };
  }, [refresh]);
  const launcher = (
      <button
        className={`mail-launcher${fightContainer ? ' mail-launcher--fight' : ''}`}
        title={String(t('mail_title'))}
        aria-label={String(t('mail_title'))}
        onClick={() => setOpen((value) => !value)}>
        <img src={`${process.env.PUBLIC_URL}/mail/open-mail.svg`} alt="" />
        {unread > 0 && <span className="mail-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
  );
  return (
    <>
      {visible && (fightContainer ? createPortal(launcher, fightContainer) : launcher)}
      {visible && open && <MailWindow aboveFight={!!fight} revision={revision} onClose={() => setOpen(false)} onChanged={refresh} />}
    </>
  );
}

export function MailWindow({ revision = 0, aboveFight = false, onClose, onChanged }: { revision?: number; aboveFight?: boolean; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation();
  const inventory = useAppSelector((state) => state.inventory.items);
  const level = useAppSelector((state) => state.stats.stats.level);
  const maySendBoundItems = useAppSelector((state) => (state.account?.id === 67 && state.account?.serverId === 1) || state.character?.titles?.current===25);
  const serverId = useAppSelector((state) => state.account?.serverId);
  const [folder, setFolder] = useState<'inbox' | 'sent' | 'compose'>('inbox');
  const [page, setPage] = useState(0);
  const [list, setList] = useState<MailList>({ messages: [], more: false, unread: 0 });
  const [selected, setSelected] = useState<Mail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [recipient, setRecipient] = useState('');
  // Presentation hint only: the server resolves the recipient's immutable identity.
  const [trade,setTrade]=useState<any>();
  const unrestrictedRecipient = (serverId === 1 && recipient.trim() === 'ANGZONA') || !!trade?.recipient?.merchant;
  useEffect(()=>{let live=true;setTrade(undefined);const refresh=()=>merchantApi({action:'status',recipient}).then(r=>{if(live)setTrade(r);}).catch(()=>{});const timer=window.setTimeout(refresh,250),poll=window.setInterval(refresh,10000);return()=>{live=false;window.clearTimeout(timer);window.clearInterval(poll);};},[recipient,revision,serverId,maySendBoundItems]);
  const items = Object.values(inventory).filter((item) =>
    (!isItemBound(item) || maySendBoundItems || unrestrictedRecipient) &&
    getItemLocation(item) >= 0 && getItemLocation(item) <= 9);
  const recipientInput = useRef<HTMLInputElement>(null);
  const subjectInput = useRef<HTMLInputElement>(null);
  const [recipientOpen, setRecipientOpen] = useState(false);
  const [recipientOptions, setRecipientOptions] = useState<string[]>([]);
  const [recipientIndex, setRecipientIndex] = useState(-1);
  useEffect(() => {
    setRecipientOptions([]);
    setRecipientIndex(-1);
    if (folder !== 'compose' || !recipientOpen || !recipient.trim()) return;
    let active = true;
    const timer = setTimeout(() => {
      mailRequest<string[]>('mailRecipients', { prefix: recipient.trim() })
        .then(names => { if (active) setRecipientOptions(names); })
        .catch(() => { if (active) setRecipientOptions([]); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [recipient, recipientOpen, folder]);
  const chooseRecipient = (name: string) => {
    setRecipient(name);
    setRecipientOpen(false);
  };
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [price, setPrice] = useState('0');
  const [slots, setSlots] = useState<(string | null)[]>(Array(5).fill(null));
  const [picker, setPicker] = useState<number | null>(null);
  const id = useRef('');
  if (!id.current) id.current = requestId();
  const [localRevision, setLocalRevision] = useState(0);
  useEffect(() => {
    if (folder === 'compose') return;
    let active = true;
    mailRequest<MailList>('mailList', { folder, page })
      .then((data) => {
        if (active) {
          setList(data);
          setSelected((current) => data.messages.find((mail) => mail.id === current?.id) ?? null);
        }
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [folder, page, revision, localRevision]);
  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'mail_error_failed');
    } finally {
      setBusy(false);
    }
  };
  const send = () =>
    run(async () => {
      if (!recipient.trim()) {
        recipientInput.current?.focus();
        throw new Error('mail_error_recipient_required');
      }
      if (!subject.trim()) {
        subjectInput.current?.focus();
        throw new Error('mail_error_subject_required');
      }
      if (!Number.isSafeInteger(Number(price)) || Number(price) < 0 || Number(price) > 2147483647 || (Number(price) > 0 && !slots.some(Boolean)))
        throw new Error('mail_error_price');
      if (slots.some(uid => uid !== null && !inventory[uid])) throw new Error('mail_error_item');
      await mailRequest('mailSend', {
        requestId: id.current,
        recipient,
        subject,
        body,
        price: Number(price),
        attachments: slots.filter(Boolean),
      });
      id.current = requestId();
      setSlots(Array(5).fill(null));
      setRecipient('');
      setSubject('');
      setBody('');
      setPrice('0');
      setNotice('mail_sent_success');
      setFolder('sent');
      setPage(0);
      setLocalRevision((value) => value + 1);
      onChanged();
    });
  const action = (name: string) =>
    run(async () => {
      if (!selected) return;
      const updated = await mailRequest<Mail>('mailAction', { id: selected.id, action: name });
      setSelected(updated);
      setLocalRevision((value) => value + 1);
      onChanged();
    });
  const openMail = (mail: Mail) => {
    setSelected(mail);
    setError('');
    setNotice('');
    if (folder === 'inbox' && !mail.read)
      mailRequest('mailAction', { id: mail.id, action: 'read' })
        .then(onChanged)
        .catch((err) => setError(err.message));
  };
  const go = (tab: typeof folder) => {
    setFolder(tab);
    setPage(0);
    setSelected(null);
    setError('');
    setNotice('');
  };
  return (
    <Panel name="mail_title" onClose={onClose} style={aboveFight ? { zIndex: 21000 } : undefined}>
      <div className="mail-window">
        <nav className="mail-tabs" aria-label={String(t('mail_title'))}>
          {(['compose', 'inbox', 'sent'] as const).map((tab) => (
            <JButton
              key={tab}
              className={folder === tab ? 'is-active' : ''}
              size={{ width: 130, height: 26 }}
              text={String(t(`mail_${tab}`))}
              disabled={busy}
              onClick={() => go(tab)}
            />
          ))}
          <div className="mail-sidebar-icon" aria-hidden="true">
            <img src={`${process.env.PUBLIC_URL}/mail/open-mail.svg`} alt="" />
          </div>
          <JButton
            className="mail-refresh"
            text={String(t('mail_refresh'))}
            disabled={busy}
            onClick={() => {
              setLocalRevision((value) => value + 1);
              onChanged();
            }}
          />
        </nav>
        <section className="mail-main">
          {error && (
            <div role="alert" className="mail-error">
              {t(error)}
            </div>
          )}
          {notice && <div role="status">{t(notice)}</div>}
          {trade?.self?.merchant&&<div role="status">{trade.self.unlimited?'สิทธิ์พ่อค้า: ส่งและรับไม่จำกัด':`สิทธิ์พ่อค้า: ส่งได้อีก ${trade.self.remaining}/5 ครั้ง · รีเซ็ตเที่ยงคืนเวลาไทย`}</div>}
          {folder === 'compose' ? (
            <div className="mail-compose">
              <div className="mail-compose-header">
                <div className="mail-stamp" aria-hidden="true">
                  <img src={`${process.env.PUBLIC_URL}/mail/open-mail.svg`} alt="" />
                </div>
                <div className="mail-address-fields">
                  <label>
                    {t('mail_recipient')}
                    <div className="mail-recipient-field">
                      <input ref={recipientInput} disabled={busy} maxLength={64} value={recipient}
                        role="combobox" aria-autocomplete="list" aria-expanded={recipientOpen && recipientOptions.length > 0}
                        aria-controls="mail-recipient-options" aria-required="true"
                        aria-activedescendant={recipientOpen && recipientIndex >= 0 ? `mail-recipient-${recipientIndex}` : undefined}
                        onFocus={() => setRecipientOpen(true)} onBlur={() => setRecipientOpen(false)}
                        onChange={(event) => { setRecipient(event.target.value); setRecipientOpen(true); }}
                        onKeyDown={event => {
                          if (event.key === 'Escape') setRecipientOpen(false);
                          if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && recipientOptions.length) {
                            event.preventDefault(); setRecipientOpen(true);
                            setRecipientIndex(index => (index + (event.key === 'ArrowDown' ? 1 : recipientOptions.length - 1)) % recipientOptions.length);
                          }
                          if (event.key === 'Enter' && recipientOpen && recipientIndex >= 0) {
                            event.preventDefault(); chooseRecipient(recipientOptions[recipientIndex]);
                          }
                        }} />
                      {recipientOpen && recipientOptions.length > 0 && (
                        <ul id="mail-recipient-options" role="listbox" className="mail-recipient-options">
                          {recipientOptions.map((name, index) => (
                            <li key={name} id={`mail-recipient-${index}`} role="option" aria-selected={index === recipientIndex}
                              onMouseDown={event => event.preventDefault()} onClick={() => chooseRecipient(name)}>{name}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </label>
                  <label>
                    {t('mail_subject')}
                    <input ref={subjectInput} aria-required="true" disabled={busy} maxLength={80} value={subject} onChange={(event) => setSubject(event.target.value)} />
                  </label>
                </div>
              </div>
              <label className="mail-message-label">
                {t('mail_body')}
                <textarea disabled={busy} maxLength={2000} value={body} onChange={(event) => setBody(event.target.value)} />
              </label>
              <div>{t('mail_attachments')}</div>
              <div className="mail-slots">
                {slots.map((uid, index) => {
                  const item = uid ? inventory[uid] : undefined;
                  return (
                    <div className="mail-slot" key={index}>
                      <button disabled={busy} aria-label={String(t('mail_slot', { slot: index + 1 }))} onClick={() => setPicker(index)}>
                        {item ? <CDNImage src={getItemSrc(item)} width={32} height={32} /> : <span>＋</span>}
                      </button>
                      {item && (
                        <>
                          <ChatItemLink item={item} />
                          <button
                            disabled={busy}
                            aria-label={String(t('mail_remove_attachment', { slot: index + 1 }))}
                            onClick={() => setSlots((current) => current.map((value, i) => (i === index ? null : value)))}>
                            ×
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <label>
                {t('mail_cod_price')}
                <input
                  disabled={busy}
                  type="number"
                  min={0}
                  max={2147483647}
                  step={1}
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                />
              </label>
              <small>{t('mail_cod_help')}</small>
              <small>{t(serverId===2?'mail_send_rules_server2':'mail_send_rules')}</small>
              <JButton
                size={{ width: 120, height: 25 }}
                text={String(t('mail_send'))}
                disabled={busy || (level<(serverId===2?40:20) && !unrestrictedRecipient && !maySendBoundItems) || (trade?.self?.merchant&&!trade.self.unlimited&&trade.self.remaining===0)}
                onClick={send}
              />
            </div>
          ) : (
            <div className="mail-content">
              <div className="mail-list">
                {list.messages.length === 0 && <p>{t('mail_empty')}</p>}
                {list.messages.map((mail) => (
                  <button key={mail.id} className={selected?.id === mail.id ? 'mail-selected' : ''} onClick={() => openMail(mail)}>
                    <strong>
                      {folder === 'inbox' && !mail.read ? '● ' : ''}
                      {mail.subject}
                    </strong>
                    <span>{folder === 'inbox' ? mail.sender : mail.recipient}</span>
                    <small>{t(`mail_state_${mail.state}`)}</small>
                  </button>
                ))}
                <div className="mail-pages">
                  <button disabled={page === 0 || busy} onClick={() => setPage((value) => value - 1)}>
                    ‹
                  </button>
                  <span>{page + 1}</span>
                  <button disabled={!list.more || busy} onClick={() => setPage((value) => value + 1)}>
                    ›
                  </button>
                </div>
              </div>
              <div className="mail-detail">
                {selected ? (
                  <>
                    <h3>{selected.subject}</h3>
                    <div>
                      {t('mail_from')}: {selected.sender}
                    </div>
                    <div>
                      {t('mail_to')}: {selected.recipient}
                    </div>
                    <small>{new Date(selected.createdAt).toLocaleString()}</small>
                    <p className="mail-body">{selected.body}</p>
                    <div>{t('mail_cod_total', { price: selected.price })}</div>
                    <div className="mail-detail-items">
                      {selected.attachments.map((item) => (
                        <div key={item.uid}>
                          <CDNImage src={getItemSrc(item)} width={30} height={30} />
                          <ChatItemLink item={item} />
                          <span> ×{getItemAmount(item)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mail-actions">
                      {folder === 'inbox' && selected.state === 'pending' && selected.attachments.length > 0 && (
                        <>
                          <JButton
                            size={{ width: 220, height: 26 }}
                            text={String(t(selected.price ? 'mail_pay_claim' : 'mail_claim', { price: selected.price }))}
                            disabled={busy}
                            onClick={() => action('claim')}
                          />
                          {selected.sender !== '[ระบบ]' && <JButton text={String(t('mail_reject'))} disabled={busy} onClick={() => action('reject')} />}
                        </>
                      )}
                      {folder === 'sent' && selected.attachments.length > 0 && ['pending', 'returned'].includes(selected.state) && (
                        <JButton
                          size={{ width: 160, height: 26 }}
                          text={String(t(selected.state === 'pending' ? 'mail_cancel' : 'mail_reclaim'))}
                          disabled={busy}
                          onClick={() => action(selected.state === 'pending' ? 'cancel' : 'claim')}
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <p>{t('mail_select_message')}</p>
                )}
              </div>
            </div>
          )}
        </section>
        {picker !== null && (
          <div className="mail-picker">
            <h3>{t('mail_pick_item')}</h3>
            <p>{t('mail_eligible_help')}</p>
            <JButton text={String(t('Close'))} onClick={() => setPicker(null)} />
            <div>
              {items
                .filter((item) => !slots.includes(item.uid) || slots[picker] === item.uid)
                .map((item) => (
                  <button
                    key={item.uid}
                    onClick={() => {
                      setSlots((current) => current.map((value, index) => (index === picker ? item.uid : value)));
                      setPicker(null);
                    }}>
                    <CDNImage src={getItemSrc(item)} width={32} height={32} />
                    <ChatItemLink item={item} />
                    <span> ×{getItemAmount(item)}</span>
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
